import { useEffect, useMemo, useState, type MouseEvent } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ReferenceLine,
} from "recharts";
import { BarChart3, Loader2, Sparkles } from "lucide-react";
import { Button, Card, EmptyState, ErrorBlock } from "@/components/ui-ext/primitives";
import { RelatedDocumentsModal } from "@/components/engineering-insights/RelatedDocumentsModal";
import {
  type EngineeringRelatedDocumentsParams,
  useEngineeringChartSummary,
  useEngineeringMetrics,
  type EngineeringInsightsGranularity,
} from "@/lib/engineering-insights/api";
import {
  collapseSquadSeries,
  combineSegmentMeanRows,
  filterDisplayableRows,
  formatCompactValue,
  formatPeriodLabel,
  formatValueWithUnit,
  limitSeriesToTopN,
  pivotSeries,
  pivotSquadComparison,
  scaleRowValues,
  seriesColor,
  type CompareSquadPivot,
  type PivotedSeries,
  type SquadMode,
} from "@/lib/engineering-insights/chart-data";
import type { EngineeringChartSpec } from "@/lib/engineering-insights/chart-specs";
import { filterRowsToRoster } from "@/lib/engineering-insights/team-roster";
import { cn } from "@/lib/utils";

/** Weeks per month, for scaling per-week targets onto monthly charts. */
const WEEKS_PER_MONTH = 52 / 12;

const GRID_STROKE = "#e2e5f3";
const AXIS_STROKE = "#989fbb";
const AXIS_FONT_SIZE = 11;
const TOOLTIP_STYLE = { borderRadius: 10, borderColor: "#e2e5f3", fontSize: 12 } as const;
const VALUE_LABEL_FILL = "#5b6285";
const VALUE_LABEL_FONT_SIZE = 10;
// Beyond this many rendered points, per-point value labels overlap into noise.
const MAX_LABELLED_POINTS = 48;
// Totals charts fold every period client-side; all totals metrics are sums,
// so fetching monthly buckets is value-identical and moves fewer rows.
const TOTALS_FETCH_GRANULARITY: EngineeringInsightsGranularity = "month";

// Meeting Mode: axis/label type at 1x is illegible on a screen-shared 1080p
// display, so presentation charts scale every chart font by this factor.
const PRESENTATION_FONT_SCALE = 1.5;

const HBAR_ROW_HEIGHT = 28;
// Presentation hbars grow rows instead of stretching bars to fill the
// viewport (fat bars read worse, not better); overflow scrolls internally.
const HBAR_PRESENTATION_ROW_HEIGHT = 40;
const HBAR_CHROME = 60;
const hbarHeight = (bars: number, presentation = false): number =>
  Math.max(
    256,
    bars * (presentation ? HBAR_PRESENTATION_ROW_HEIGHT : HBAR_ROW_HEIGHT) + HBAR_CHROME,
  );

const SQUAD_PANEL_LABELS = { member: "Member", b2b: "B2B" } as const;

interface DataPointSelection {
	period?: string;
	label?: string;
	seriesKey?: string;
	x: number;
	y: number;
}

interface ChartMenuState extends DataPointSelection {
	documentsParams: EngineeringRelatedDocumentsParams;
}

interface PlotOptions {
  spec: EngineeringChartSpec;
  data: PivotedSeries["data"];
  seriesKeys: string[];
  granularity: EngineeringInsightsGranularity;
  showLegend: boolean;
  showValueLabels: boolean;
  /** Shared axis max for compare panels; single plots auto-scale. */
  valueDomainMax?: number;
  overlayValues?: Array<{ label: string; value: number }>;
  onPointClick?: (selection: DataPointSelection) => void;
  /** Multiplies every chart font (axes, labels, tooltip, legend). */
  fontScale?: number;
  /**
   * Presentation disables the entry animation: slide flips should not
   * redraw-dance, and labels stay hidden until the animation ends.
   */
  animate?: boolean;
}

const recordPayload = (value: unknown): Record<string, unknown> | undefined => {
	if (value && typeof value === "object") {
		return value as Record<string, unknown>;
	}
	return undefined;
};

const payloadFromEntry = (entry: unknown): Record<string, unknown> | undefined => {
	const payload = recordPayload(entry);
	return recordPayload(payload?.payload) ?? payload;
};

const pointSelectionFromEntry = (
	entry: unknown,
	seriesKey: string | undefined,
	event: MouseEvent
): DataPointSelection | null => {
	const payload = payloadFromEntry(entry);
	const period = typeof payload?.period === "string" ? payload.period : undefined;
	const label = typeof payload?.label === "string" ? payload.label : undefined;
	if (!period && !label) {
		return null;
	}
	return {
		period,
		label,
		seriesKey,
		x: event.clientX,
		y: event.clientY
	};
};

const pointSelectionFromChartState = (
	state: unknown,
	event: MouseEvent
): DataPointSelection | null => {
	const payload = recordPayload(state);
	const activePayload = payload?.activePayload;
	if (!Array.isArray(activePayload) || activePayload.length === 0) {
		return null;
	}
	const first = recordPayload(activePayload[0]);
	const seriesKey =
		typeof first?.dataKey === "string" ? first.dataKey : undefined;
	return pointSelectionFromEntry(first, seriesKey, event);
};

function renderPlot({
  spec,
  data,
  seriesKeys,
  granularity,
  showLegend,
  showValueLabels,
  valueDomainMax,
  overlayValues = [],
  onPointClick,
  fontScale = 1,
  animate = true,
}: PlotOptions) {
  const domain = valueDomainMax ? ([0, valueDomainMax] as [number, number]) : undefined;
  const formatPeriodTick = (value: unknown): string =>
    formatPeriodLabel(String(value), granularity);
  const formatValueLabel = (value: unknown): string => formatCompactValue(Number(value));
  const formatTooltipValue = (value: unknown): string =>
    formatValueWithUnit(Number(value), spec.unit);
  // Per-week targets scale onto monthly charts; absolute units don't move.
  const target = spec.target
    ? {
        value:
          spec.target.perWeek && granularity === "month"
            ? Math.round(spec.target.value * WEEKS_PER_MONTH * 10) / 10
            : spec.target.value,
        label: spec.target.label,
      }
    : null;
  const axisFontSize = Math.round(AXIS_FONT_SIZE * fontScale);
  const valueLabelStyle = {
    fontSize: Math.round(VALUE_LABEL_FONT_SIZE * fontScale),
    fill: VALUE_LABEL_FILL,
  };
  const overlayLabelFontSize = Math.round(10 * fontScale);
  const tooltipStyle = { ...TOOLTIP_STYLE, fontSize: Math.round(TOOLTIP_STYLE.fontSize * fontScale) };
  const legendWrapperStyle = { fontSize: Math.round(12 * fontScale) };

  if (spec.chartType === "horizontalBar") {
    return (
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 32 }}>
        <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
        <XAxis type="number" stroke={AXIS_STROKE} fontSize={axisFontSize} domain={domain} />
        <YAxis
          type="category"
          dataKey="label"
          stroke={AXIS_STROKE}
          fontSize={axisFontSize}
          width={Math.round(220 * fontScale)}
          interval={0}
        />
        <Tooltip contentStyle={tooltipStyle} formatter={formatTooltipValue} />
        <Bar
          dataKey="value"
          fill={seriesColor(0)}
          radius={[0, 6, 6, 0]}
          isAnimationActive={animate}
          onClick={(entry: unknown, _index: number, event: MouseEvent) => {
            const selection = pointSelectionFromEntry(entry, "value", event);
            if (selection) {
              onPointClick?.(selection);
            }
          }}
        >
          <LabelList
            dataKey="value"
            position="right"
            style={valueLabelStyle}
            formatter={formatValueLabel}
          />
        </Bar>
      </BarChart>
    );
  }
  if (spec.chartType === "line") {
    return (
      <LineChart
        data={data}
        margin={{ top: 12 }}
        onClick={(state: unknown, event: MouseEvent) => {
          const selection = pointSelectionFromChartState(state, event);
          if (selection) {
            onPointClick?.(selection);
          }
        }}
      >
        <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
        <XAxis
          dataKey="period"
          stroke={AXIS_STROKE}
          fontSize={axisFontSize}
          tickFormatter={formatPeriodTick}
          // Inset edge points so value labels on the first/last dots stay
          // inside the clipped chart surface (20px fits half of the widest
          // compact label, e.g. "999.9B").
          padding={{ left: 20, right: 20 }}
        />
        <YAxis stroke={AXIS_STROKE} fontSize={axisFontSize} padding={{ top: 16 }} domain={domain} />
        <Tooltip
          contentStyle={tooltipStyle}
          labelFormatter={formatPeriodTick}
          formatter={formatTooltipValue}
        />
        {overlayValues.map((overlay) => (
          <ReferenceLine
            key={overlay.label}
            y={overlay.value}
            stroke={AXIS_STROKE}
            strokeDasharray="4 4"
            label={{ value: overlay.label, fontSize: overlayLabelFontSize, fill: AXIS_STROKE }}
          />
        ))}
        {target && (
          <ReferenceLine
            y={target.value}
            stroke="var(--primary)"
            strokeDasharray="6 4"
            ifOverflow="extendDomain"
            label={{
              value: target.label,
              position: "insideTopRight",
              fontSize: overlayLabelFontSize,
              fill: "var(--primary)",
            }}
          />
        )}
        {showLegend && <Legend wrapperStyle={legendWrapperStyle} />}
        {seriesKeys.map((key, index) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={seriesColor(index)}
            strokeWidth={2}
            dot={{ r: 3, strokeWidth: 2, fill: "#fff" }}
            activeDot={{ r: 4 }}
            connectNulls
            isAnimationActive={animate}
          >
            {showValueLabels && (
              <LabelList
                dataKey={key}
                position="top"
                offset={8}
                style={valueLabelStyle}
                formatter={formatValueLabel}
              />
            )}
          </Line>
        ))}
      </LineChart>
    );
  }
  const stacked = spec.chartType === "stackedBar";
  return (
    <BarChart data={data} margin={{ top: 12 }}>
      <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
      <XAxis
        dataKey="period"
        stroke={AXIS_STROKE}
        fontSize={axisFontSize}
        tickFormatter={formatPeriodTick}
      />
      {/* Top inset keeps value labels above the tallest bar inside the
          clipped chart surface. */}
      <YAxis stroke={AXIS_STROKE} fontSize={axisFontSize} padding={{ top: 16 }} domain={domain} />
      <Tooltip
          contentStyle={tooltipStyle}
          labelFormatter={formatPeriodTick}
          formatter={formatTooltipValue}
        />
      {overlayValues.map((overlay) => (
        <ReferenceLine
          key={overlay.label}
          y={overlay.value}
          stroke={AXIS_STROKE}
          strokeDasharray="4 4"
          label={{ value: overlay.label, fontSize: overlayLabelFontSize, fill: AXIS_STROKE }}
        />
      ))}
      {target && (
        <ReferenceLine
          y={target.value}
          stroke="var(--primary)"
          strokeDasharray="6 4"
          ifOverflow="extendDomain"
          label={{
            value: target.label,
            position: "insideTopRight",
            fontSize: overlayLabelFontSize,
            fill: "var(--primary)",
          }}
        />
      )}
      {showLegend && <Legend wrapperStyle={legendWrapperStyle} />}
      {seriesKeys.map((key, index) => (
        <Bar
          key={key}
          dataKey={key}
          stackId={stacked ? "a" : undefined}
          fill={seriesColor(index)}
          radius={stacked ? undefined : [6, 6, 0, 0]}
          isAnimationActive={animate}
          onClick={(entry: unknown, _index: number, event: MouseEvent) => {
            const selection = pointSelectionFromEntry(entry, key, event);
            if (selection) {
              onPointClick?.(selection);
            }
          }}
        >
          {!stacked && showValueLabels && (
            <LabelList
              dataKey={key}
              position="top"
              style={valueLabelStyle}
              formatter={formatValueLabel}
            />
          )}
        </Bar>
      ))}
    </BarChart>
  );
}

function SharedLegend({ keys, presentation }: { keys: string[]; presentation?: boolean }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
      {keys.map((key, index) => (
        <span
          key={key}
          className={cn(
            "inline-flex items-center gap-1.5 text-muted-foreground",
            presentation ? "text-sm" : "text-xs",
          )}
        >
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: seriesColor(index) }} />
          {key}
        </span>
      ))}
    </div>
  );
}

const squadPromptPhrase = (squadMode: SquadMode): string => {
  if (squadMode === "compare") return " comparing the member and b2b squads";
  if (squadMode === "all") return " across squads";
  return ` for the ${squadMode} squad`;
};

export function EngineeringChart({
  spec,
  from,
  to,
  squadMode,
  granularity,
  onAsk,
  presentation = false,
}: {
  spec: EngineeringChartSpec;
  from: string;
  to: string;
  squadMode: SquadMode;
  granularity: EngineeringInsightsGranularity;
  onAsk?: (prompt: string) => void;
  /** Meeting Mode: fill the container, scale fonts, drop page-only chrome. */
  presentation?: boolean;
}) {
  const summary = useEngineeringChartSummary();
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [menu, setMenu] = useState<ChartMenuState | null>(null);
  const [modalParams, setModalParams] = useState<EngineeringRelatedDocumentsParams | null>(null);
  const squadParam =
    spec.squadFilter !== false && (squadMode === "member" || squadMode === "b2b")
      ? squadMode
      : undefined;
  const effectiveGranularity = spec.totals ? TOTALS_FETCH_GRANULARITY : granularity;
  const metrics = useEngineeringMetrics(spec.metricKey, {
    from,
    to,
    squad: squadParam,
    granularity: effectiveGranularity,
    includeOverlays: spec.showOverlays === true,
  });

  const rows = useMemo(() => {
    let processed = filterDisplayableRows(
      (metrics.data?.rows ?? []).filter((row) => row.segmentKey !== "overlay_type"),
      { includeNonDisplaySquads: spec.squadFilter === false },
    );
    if (spec.series === "engineer") {
      processed = filterRowsToRoster(processed);
    }
    if (spec.combineSegmentsMean) {
      processed = combineSegmentMeanRows(processed);
    }
    if (spec.valueScale) {
      processed = scaleRowValues(processed, spec.valueScale);
    }
    return processed;
  }, [metrics.data, spec]);
  const overlayValues = useMemo(() => {
    const overlays = filterDisplayableRows(
      (metrics.data?.rows ?? []).filter((row) => row.segmentKey === "overlay_type"),
      { includeNonDisplaySquads: spec.squadFilter === false },
    );
    const byLabel = new Map<string, number>();
    for (const row of overlays) {
      byLabel.set(row.segmentValue ?? "target", row.value);
    }
    return [...byLabel.entries()].map(([label, value]) => ({ label, value }));
  }, [metrics.data, spec.squadFilter]);
  const isComparePanels =
    spec.squadFilter !== false && squadMode === "compare" && spec.series !== "squad";
  const single = useMemo(() => {
    const pivot =
      pivotSeries(
        squadMode === "all" && spec.series === "squad" ? collapseSquadSeries(rows) : rows,
        spec,
      );
    return spec.topNSeries ? limitSeriesToTopN(pivot, spec.topNSeries) : pivot;
  }, [rows, spec, squadMode]);
  const compare: CompareSquadPivot | null = useMemo(
    () => (isComparePanels ? pivotSquadComparison(rows, spec) : null),
    [isComparePanels, rows, spec],
  );

  useEffect(() => {
    setSummaryText(null);
    setMenu(null);
    setModalParams(null);
  }, [from, to, squadMode, granularity, spec.id]);

  const explorePrompt =
    `Looking at the "${spec.title}" engineering metric (${spec.metricKey}, ` +
    `${spec.valueLabel.toLowerCase()}) from ${from} to ${to}` +
    `${squadPromptPhrase(squadMode)}: ` +
    "what stands out and what should we investigate?";
  const explore = () => onAsk?.(explorePrompt);

  const isHbar = spec.chartType === "horizontalBar";
  const fullWidth = spec.fullWidth === true || isHbar;
  const fontScale = presentation ? PRESENTATION_FONT_SCALE : 1;

  const buildDocumentsParams = (selection: DataPointSelection): EngineeringRelatedDocumentsParams => {
    const params: EngineeringRelatedDocumentsParams = {
      metricKey: spec.metricKey,
      granularity: effectiveGranularity,
    };
    if (spec.totals) {
      params.from = from;
      params.to = to;
    } else if (selection.period) {
      params.period = selection.period;
    }
    const clickedSeries =
      selection.seriesKey && selection.seriesKey !== "value" ? selection.seriesKey : selection.label;
    if (spec.series === "engineer" && clickedSeries) {
      params.engineer = clickedSeries;
    } else if (spec.series === "segmentValue" && clickedSeries && clickedSeries !== "Other") {
      params.segmentValue = clickedSeries;
    } else if (spec.series === "squad" && squadMode !== "all" && clickedSeries) {
      params.squad = clickedSeries;
    }
    if (!params.squad && (squadMode === "member" || squadMode === "b2b")) {
      params.squad = squadMode;
    }
    return params;
  };

  const handlePointClick = (selection: DataPointSelection) => {
    // Operational metrics have no related-entity mapping on the server.
    if (spec.relatedDocuments === false) {
      return;
    }
    const documentsParams = buildDocumentsParams(selection);
    if (!documentsParams.period && !(documentsParams.from && documentsParams.to)) {
      return;
    }
    setMenu({ ...selection, documentsParams });
  };

  const summarize = () => {
    summary.mutate(
      {
        metricKey: spec.metricKey,
        title: spec.title,
        description: spec.description,
        valueLabel: spec.valueLabel,
        from,
        to,
        squad: squadParam,
        granularity: effectiveGranularity,
      },
      {
        onSuccess: (response) => setSummaryText(response.summary),
      }
    );
  };

  const renderSingle = () => {
    const showLegend =
      spec.series !== "none" && !spec.totals && single.seriesKeys.length > 1;
    const showValueLabels =
      single.data.length * Math.max(single.seriesKeys.length, 1) <= MAX_LABELLED_POINTS;
    const height = isHbar ? hbarHeight(single.data.length, presentation) : 256;
    const plot = (
      <ResponsiveContainer width="100%" height="100%">
        {renderPlot({
          spec,
          data: single.data,
          seriesKeys: single.seriesKeys,
          granularity: effectiveGranularity,
          showLegend,
          showValueLabels,
          overlayValues,
          onPointClick: handlePointClick,
          fontScale,
          animate: !presentation,
        })}
      </ResponsiveContainer>
    );
    if (!presentation) {
      return <div style={{ height }}>{plot}</div>;
    }
    if (isHbar) {
      // Natural row height, centered; taller-than-viewport rosters scroll.
      return (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="my-auto w-full shrink-0" style={{ height }}>
            {plot}
          </div>
        </div>
      );
    }
    return <div className="min-h-0 flex-1">{plot}</div>;
  };

  const renderCompare = (pivot: CompareSquadPivot) => {
    // Half-width panels get half the label budget; both panels toggle together.
    const showValueLabels = pivot.panels.every(
      (panel) =>
        panel.data.length * Math.max(pivot.seriesKeys.length, 1) <=
        MAX_LABELLED_POINTS / 2,
    );
    const height = isHbar
      ? hbarHeight(
          Math.max(...pivot.panels.map((panel) => panel.data.length)),
          presentation,
        )
      : 256;
    const panelPlot = (panel: CompareSquadPivot["panels"][number]) => (
      <ResponsiveContainer width="100%" height="100%">
        {renderPlot({
          spec,
          data: panel.data,
          seriesKeys: pivot.seriesKeys,
          granularity: effectiveGranularity,
          showLegend: false,
          showValueLabels,
          valueDomainMax: pivot.maxValue,
          overlayValues,
          onPointClick: handlePointClick,
          fontScale,
          animate: !presentation,
        })}
      </ResponsiveContainer>
    );
    const panelBody = (panel: CompareSquadPivot["panels"][number]) => {
      if (panel.data.length === 0) {
        return (
          <div
            style={presentation ? undefined : { height }}
            className={cn(
              "grid place-items-center rounded-[10px] border border-dashed border-soft text-muted-foreground",
              presentation ? "min-h-0 flex-1 text-sm" : "text-xs",
            )}
          >
            No data
          </div>
        );
      }
      if (!presentation) {
        return <div style={{ height }}>{panelPlot(panel)}</div>;
      }
      if (isHbar) {
        return (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="my-auto w-full shrink-0" style={{ height }}>
              {panelPlot(panel)}
            </div>
          </div>
        );
      }
      return <div className="min-h-0 flex-1">{panelPlot(panel)}</div>;
    };
    return (
      <>
        <div
          className={cn(
            "grid grid-cols-1 sm:grid-cols-2 gap-4",
            presentation && "min-h-0 flex-1",
          )}
        >
          {pivot.panels.map((panel) => (
            <div
              key={panel.squad}
              className={cn("min-w-0", presentation && "flex min-h-0 flex-col")}
            >
              <div
                className={cn(
                  "mb-1 font-semibold text-anchor",
                  presentation ? "text-sm" : "text-xs",
                )}
              >
                {SQUAD_PANEL_LABELS[panel.squad]}
              </div>
              {panelBody(panel)}
            </div>
          ))}
        </div>
        {!spec.totals && pivot.seriesKeys.length > 1 && (
          <SharedLegend keys={pivot.seriesKeys} presentation={presentation} />
        )}
      </>
    );
  };

  const renderBody = () => {
    if (metrics.isLoading || metrics.isPending) {
      return (
        <div
          className={cn(
            "w-full rounded-[10px] bg-muted animate-pulse",
            presentation ? "min-h-0 flex-1" : "h-64",
          )}
        />
      );
    }
    if (metrics.isError) {
      return (
        <ErrorBlock
          message={metrics.error instanceof Error ? metrics.error.message : "Failed to load metric."}
          onRetry={() => void metrics.refetch()}
        />
      );
    }
    const isEmpty = compare
      ? compare.panels.every((panel) => panel.data.length === 0)
      : single.data.length === 0;
    if (isEmpty) {
      return (
        <EmptyState
          title="No data for this period"
          body="Nothing has been ingested for this metric in the selected range yet."
          icon={BarChart3}
        />
      );
    }
    return compare ? renderCompare(compare) : renderSingle();
  };

  return (
    <Card
      className={cn(
        "p-5 flex flex-col gap-3",
        !presentation && fullWidth && "lg:col-span-2",
        presentation && "h-full min-h-0 p-6",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {presentation && (
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--primary)]">
              {spec.section}
            </div>
          )}
          <h3
            className={cn(
              "font-semibold text-anchor",
              presentation ? "text-xl" : "text-sm",
            )}
          >
            {spec.title}
          </h3>
          <p
            className={cn(
              "mt-0.5 text-muted-foreground",
              presentation ? "text-sm" : "text-xs",
            )}
          >
            {spec.description}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            disabled={summary.isPending}
            onClick={summarize}
          >
            {summary.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Summarize
          </Button>
          {!presentation && (
            <Button variant="ghost" size="sm" className="shrink-0" onClick={explore}>
              Explore →
            </Button>
          )}
        </div>
      </div>
      {renderBody()}
      {summary.isError && (
        <div className="rounded-[10px] border border-[var(--danger)]/20 bg-[var(--danger)]/5 px-3 py-2 text-xs text-[var(--danger)]">
          {summary.error instanceof Error ? summary.error.message : "Failed to summarize chart."}
        </div>
      )}
      {summaryText && (
        <p
          className={cn(
            "rounded-[10px] border border-soft bg-muted/50 px-3 py-2 leading-relaxed text-anchor",
            presentation ? "text-base" : "text-sm",
          )}
        >
          {summaryText}
        </p>
      )}
      <div className={cn("text-muted-foreground", presentation ? "text-sm" : "text-[11px]")}>
        {spec.valueLabel}
        {spec.totals ? " · totals for selected range" : ` · per ${granularity}`}
      </div>
      {menu && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label="Close chart context menu"
            onClick={() => setMenu(null)}
          />
          <div
            className="fixed z-50 rounded-[10px] border border-soft bg-surface p-1 shadow-card"
            style={{ left: menu.x, top: menu.y }}
          >
            <button
              type="button"
              className="rounded-md px-3 py-2 text-left text-sm text-anchor hover:bg-muted"
              onClick={() => {
                setModalParams(menu.documentsParams);
                setMenu(null);
              }}
            >
              Show related data points
            </button>
          </div>
        </>
      )}
      <RelatedDocumentsModal
        open={modalParams !== null}
        onOpenChange={(open) => {
          if (!open) {
            setModalParams(null);
          }
        }}
        params={modalParams}
        title={spec.title}
      />
    </Card>
  );
}
